import { getCandidateName } from './candidateDisplay'
import type { AvailabilityRequestSource, SpareCandidate } from './types'

/** The players one POST asks — one POST files every ask under one source. */
export interface AvailabilityRequestGroup {
  source: AvailabilityRequestSource
  playerIds: string[]
  playerNames: string[]
}

export interface AvailabilityRequestGroupOutcome {
  group: AvailabilityRequestGroup
  ok: boolean
  error?: unknown
}

/**
 * SN-8309 — split a selection into one group per candidate `source`, in the
 * order the sources first appear. A selected id with no candidate (it left the
 * list) is dropped: there is nothing left to say where its ask belongs.
 */
export const groupBySource = (
  candidates: readonly SpareCandidate[],
  playerIds: readonly string[]
): AvailabilityRequestGroup[] => {
  const groups = new Map<AvailabilityRequestSource, AvailabilityRequestGroup>()

  playerIds.forEach((id) => {
    const candidate = candidates.find((entry) => entry.id === id)
    if (!candidate) {
      return
    }
    const group = groups.get(candidate.source) ?? {
      source: candidate.source,
      playerIds: [],
      playerNames: [],
    }
    group.playerIds.push(id)
    group.playerNames.push(getCandidateName(candidate))
    groups.set(candidate.source, group)
  })

  return [...groups.values()]
}

/** A POST the request's own time limit cut off: the network stopped answering. */
const timedOut = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { name?: unknown }).name === 'AbortError'

/**
 * Send the groups one after another, recording each one's outcome — a failed
 * group doesn't stop the rest, so the asks that did go out are kept and only the
 * failed groups need a retry. The exception is a group the time limit cut off:
 * the network stopped answering, so the rest aren't tried (each would wait out
 * its own limit) and go to the retry as well. The server's ask is idempotent (an
 * open or answered ask is skipped, never re-notified), so a retry is harmless.
 */
export const sendInGroups = async (
  groups: readonly AvailabilityRequestGroup[],
  send: (group: AvailabilityRequestGroup) => Promise<unknown>
): Promise<AvailabilityRequestGroupOutcome[]> => {
  const outcomes: AvailabilityRequestGroupOutcome[] = []
  let stalled: unknown = null

  for (const group of groups) {
    if (stalled !== null) {
      outcomes.push({ group, ok: false, error: stalled })
      continue
    }
    try {
      await send(group)
      outcomes.push({ group, ok: true })
    } catch (error) {
      outcomes.push({ group, ok: false, error })
      if (timedOut(error)) {
        stalled = error
      }
    }
  }

  return outcomes
}

/** The groups a retry should resend. */
export const failedGroups = (
  outcomes: readonly AvailabilityRequestGroupOutcome[]
): AvailabilityRequestGroup[] =>
  outcomes.filter((outcome) => !outcome.ok).map((outcome) => outcome.group)

/** Everyone a failed send left un-asked, for the error message. */
export const unsentPlayerNames = (
  outcomes: readonly AvailabilityRequestGroupOutcome[]
): string[] => failedGroups(outcomes).flatMap((group) => group.playerNames)

/**
 * The error a send leaves behind, naming everyone not asked — and, when some
 * groups did go out, saying so, since those asks now show on the list. Null
 * when every group went out.
 */
export const sendFailureMessage = (
  outcomes: readonly AvailabilityRequestGroupOutcome[]
): string | null => {
  const unsent = unsentPlayerNames(outcomes)
  if (unsent.length === 0) {
    return null
  }
  const someSent = outcomes.some((outcome) => outcome.ok)

  return `Couldn’t send to ${unsent.join(', ')}.${
    someSent ? ' The other requests were sent.' : ''
  }`
}
